import { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { useNavigate, useParams } from 'react-router-dom'

import { useClient } from '@hooks/useClient'
import { useAlert } from '@hooks/useAlert'
import { getErrorMessage } from '@libs/utils'
import type { BadgeManagementFormType } from '@models/badge/BadgeManagementFormType'
import type { BadgeManagementAddPageProps } from './interface'
import { BookBadgeCategoryTypeEnum } from '../BadgeListPage/interface'

export function withBadgeManagementAddPage(
  Component: React.FC<BadgeManagementAddPageProps>
) {
  function WithBadgeManagementAddPage() {
    const client = useClient()
    const alert = useAlert()
    const navigate = useNavigate()
    const { type } = useParams()
    const [tab, setTab] = useState<BookBadgeCategoryTypeEnum>(
      (type?.toUpperCase() ??
        BookBadgeCategoryTypeEnum.EXCLUSIVE) as BookBadgeCategoryTypeEnum
    )
    const initialValues = {
      name: '',
      upload: {
        url: '',
        blob: undefined,
      },
    }

    const { mutate: createBadge, isLoading } = useMutation(
      async (form: BadgeManagementFormType) => {
        const id = await client!.badgeClient.createBookBadgeCategory({
          name: form.name,
          bookBadgeCategoryType: tab,
        })

        if (!id) {
          throw new Error('Failed to create badge')
        }

        try {
          const res = await client!.badgeClient.uploadBookBadgeCategoryImage({
            id,
            name: form.name,
            upload: form.upload,
          })

          return res
        } catch (error) {
          await client!.badgeClient.removeBookBadgeCategory({ id })
          throw error
        }
      },
      {
        onSuccess: () => {
          alert.success('สร้าง Badge สำเร็จ')
          navigate({
            pathname: `/badge/badge-management/${tab.toLowerCase()}`,
          })
        },
        onError: error => {
          alert.error(getErrorMessage(error))
        },
      }
    )

    useEffect(() => {
      const formattedType =
        type?.toUpperCase() ?? ('' as BookBadgeCategoryTypeEnum)
      if (
        Object.values(BookBadgeCategoryTypeEnum).includes(
          formattedType as BookBadgeCategoryTypeEnum
        )
      ) {
        setTab(formattedType as BookBadgeCategoryTypeEnum)
      } else {
        setTab(BookBadgeCategoryTypeEnum.EXCLUSIVE)
      }

      /* eslint-disable react-hooks/exhaustive-deps */
    }, [])

    async function onSubmit(form: BadgeManagementFormType) {
      createBadge(form)
    }

    const newProps = {
      initialValues,
      onSubmit,
      tab,
      isLoading,
    }
    return <Component {...newProps} />
  }

  return WithBadgeManagementAddPage
}
